import React from 'react';
import { Box, Container, Typography, Stack, Button, Divider } from '@mui/material';
import Grid from '@mui/material/Grid';
import ShieldIcon from '@mui/icons-material/Shield';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ShareIcon from '@mui/icons-material/Share';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import HandshakeIcon from '@mui/icons-material/Handshake';
import Header from './Header';
import SEO from './SEO';
import Footer from './Footer';
import { useNavigate } from 'react-router-dom';

const carrierBenefits = [
  {
    icon: <TrendingDownIcon sx={{ color: '#5ce1e6', fontSize: 24 }} />,
    title: 'Lower loss ratio',
    body: 'Fewer claims, smaller settlements. SMCLA-monitored facilities represent a materially different risk profile than unmonitored ones. That difference goes straight to your combined ratio.',
  },
  {
    icon: <ShieldIcon sx={{ color: '#5ce1e6', fontSize: 24 }} />,
    title: 'Premium discount weapon',
    body: 'Offer a verified safety discount tier that competitors cannot match. Win and retain the operators who care most about running a genuinely safe facility.',
  },
  {
    icon: <ShareIcon sx={{ color: '#5ce1e6', fontSize: 24 }} />,
    title: 'New revenue stream',
    body: 'Revenue share on every SMCLA deployment in your book. Safety performance generates income, not just cost avoidance.',
  },
  {
    icon: <EmojiEventsIcon sx={{ color: '#5ce1e6', fontSize: 24 }} />,
    title: 'First-mover advantage',
    body: 'The carrier that defines childcare AI safety standards sets the market. Your competitors will follow. You will have led — and locked in founding partner terms that are not available to anyone who comes after.',
  },
];

export default function InsurancePartnerPage() {
  const navigate = useNavigate();
  const toContact = () => navigate('/contact');

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO title="Insurance Partners — Reduce Childcare Risk with AI" description="SMCLA helps insurers reduce claims exposure in childcare. Real-time detection, forensic documentation, and behavioral pattern data reduce liability." path="/insurance-partners" />
      <Header onBookDemo={toContact} />

      {/* Hero */}
      <Box sx={{ position: 'relative', py: { xs: 10, md: 16 }, px: 3, textAlign: 'center', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: '-10%', left: '50%', transform: 'translateX(-50%)', width: 700, height: 700, borderRadius: '50%', background: 'radial-gradient(circle, rgba(92,225,230,0.07) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', borderRadius: 10, px: 2, py: 0.75, mb: 3 }}>
            <HandshakeIcon sx={{ color: '#5ce1e6', fontSize: 14 }} />
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500, fontSize: 12, color: '#5ce1e6', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Insurance Partnership Program
            </Typography>
          </Box>

          <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2.2rem', md: '4rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', mb: 2, lineHeight: 1.05 }}>
            Childcare claims are rising.<br />
            <Box component="span" sx={{ color: '#5ce1e6' }}>Help us stop them.</Box>
          </Typography>

          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 16, md: 20 }, color: '#9499b0', maxWidth: 620, mx: 'auto', lineHeight: 1.65, mb: 5 }}>
            SMCLA detects abuse and safety incidents before they become claims. We are building our first insurance partnership to bring verified risk reduction to the childcare liability market — together.
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={toContact}
            sx={{ px: 5, py: 1.5, fontWeight: 700, fontSize: 16, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, boxShadow: '0 0 24px rgba(92,225,230,0.25)', '&:hover': { bgcolor: '#4ecfd4' } }}
          >
            Request a Partnership Conversation →
          </Button>

          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontStyle: 'italic', fontSize: 13, color: '#ff9f43', mt: 2 }}>
            We are currently in design partner phase. One carrier will co-shape this program.
          </Typography>
        </Container>
      </Box>

      {/* Market problem */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2 }}>
            <WarningAmberIcon sx={{ color: '#ff9f43', fontSize: 18 }} />
            <Typography variant="overline" sx={{ color: '#ff9f43', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em' }}>
              The Claims Landscape
            </Typography>
          </Box>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.8rem' }, color: '#f0f0f8', letterSpacing: '-0.03em', mb: 4, lineHeight: 1.1 }}>
            The childcare liability market is under pressure.
          </Typography>

          <Grid container spacing={0} sx={{ mb: 5 }}>
            {[
              { stat: '$16M', label: 'Largest recent single daycare settlement', sub: 'South Carolina, 2024', source: 'Benton Law Firm' },
              { stat: '300–1,000%', label: 'Premium increases reported by some providers in 2024', sub: '62% of operators report difficulty finding affordable coverage', source: 'Bipartisan Policy Center' },
              { stat: '5,321', label: 'Abuse and neglect cases reported across 39 states in one year', sub: '651 children seriously injured or killed', source: 'State licensing data' },
            ].map(({ stat, label, sub, source }, i) => (
              <Grid key={stat} size={{ xs: 12, md: 4 }}>
                <Box sx={{
                  p: { xs: 3, md: 3.5 },
                  bgcolor: '#13131f',
                  border: '1px solid rgba(255,159,67,0.12)',
                  borderRight: { md: i < 2 ? 'none' : '1px solid rgba(255,159,67,0.12)' },
                  borderRadius: i === 0 ? { xs: '12px 12px 0 0', md: '12px 0 0 12px' } : i === 2 ? { xs: '0 0 12px 12px', md: '0 12px 12px 0' } : { xs: 0, md: 0 },
                }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2rem', md: '2.4rem' }, color: '#ff9f43', lineHeight: 1, mb: 1 }}>
                    {stat}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#f0f0f8', mb: 0.75, lineHeight: 1.4 }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', mb: 1, lineHeight: 1.5 }}>
                    {sub}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, color: 'rgba(148,153,176,0.55)', letterSpacing: '0.04em' }}>
                    Source: {source}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Documented settlements */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="overline" sx={{ display: 'block', color: '#ff9f43', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5, mt: 2 }}>
              Documented Settlements
            </Typography>
            <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.5rem', md: '2rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 1.5 }}>
              These are the cases that hit insurance.
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7, mb: 4, maxWidth: 660 }}>
              These are not hypotheticals. Each of these settlements was paid — in part or in full — by a daycare's insurer. The pattern is consistent: insufficient visibility into staff behavior, delayed detection, and claims that could have been stopped before they started.
            </Typography>

            <Grid container spacing={2.5} sx={{ mb: 4 }}>
              {[
                {
                  amount: '$25M',
                  location: 'Olympia Early Learning Center, WA',
                  incident: '6 children sexually abused by a caregiver over an extended period',
                  insurer: 'The daycare was severely underinsured. The carrier opposed the settlement. The court ruled the insurer liable for the full amount via bad faith litigation.',
                  accent: '#ff9f43',
                  source: 'PCVA Law',
                  url: 'https://pcva.law/news/25m-settlement-reached-in-olympia-early-learning-center-sex-abuse-lawsuit/',
                  badge: 'Bad faith ruling against carrier',
                },
                {
                  amount: '$16M',
                  location: 'Carolina Kids Child Development Center, Rock Hill, SC (2024)',
                  incident: 'Infant death from unsafe sleep practices — placed on side instead of back',
                  insurer: 'Largest South Carolina infant wrongful death settlement on record. Parent company Cadence Education adopted new protocols only after settlement.',
                  accent: '#ff9f43',
                  source: 'Yarborough Applegate',
                  url: 'https://www.yarboroughapplegate.com/results/carolina-kids-rock-hill-daycare-infant-death-lasuit/',
                  badge: 'Largest SC infant wrongful death ever',
                },
                {
                  amount: '$13.5M',
                  location: 'Kids Quest, Grand Casino Mille Lacs, MN',
                  incident: '3-year-old left unsupervised for 90+ minutes; physically and sexually assaulted',
                  insurer: 'Hennepin County jury verdict after two-week trial. Facility closed within one week of the incident becoming public.',
                  accent: '#ff9f43',
                  source: 'Grand Forks Herald',
                  url: 'https://www.grandforksherald.com/newsmd/daycare-sexual-assault-verdict-of-13-5-million-returned-in-minnesota',
                  badge: 'Jury verdict — not settlement',
                },
                {
                  amount: '$500K',
                  location: 'Church daycare, New York',
                  incident: '3-year-old fractured arm due to negligent supervision',
                  insurer: 'Insurer offered $75K, then $125K, then $300K. Plaintiffs rejected all three and recovered the full $500K policy limit. Early lowball offers exhausted to maximum.',
                  accent: '#8c52ff',
                  source: 'work4youlaw.com',
                  url: 'https://www.work4youlaw.com/verdicts-settlements/500000-5/',
                  badge: 'Policy limit fully exhausted',
                },
              ].map(({ amount, location, incident, insurer, accent, source, url, badge }) => (
                <Grid key={amount + location} size={{ xs: 12, md: 6 }}>
                  <Box sx={{ p: 3.5, bgcolor: '#0d0d18', border: `1px solid ${accent}20`, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2rem', md: '2.5rem' }, color: accent, lineHeight: 1 }}>
                        {amount}
                      </Typography>
                      <Box sx={{ bgcolor: `${accent}12`, border: `1px solid ${accent}30`, borderRadius: 10, px: 1.5, py: 0.5, flexShrink: 0 }}>
                        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 10, fontWeight: 600, color: accent, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                          {badge}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 13, color: '#f0f0f8', mb: 0.75, lineHeight: 1.4 }}>
                      {location}
                    </Typography>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', mb: 2, lineHeight: 1.6 }}>
                      {incident}
                    </Typography>
                    <Box sx={{ mt: 'auto', pt: 2, borderTop: `1px solid ${accent}15` }}>
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: '#d0d0e0', lineHeight: 1.6, fontStyle: 'italic' }}>
                        {insurer}
                      </Typography>
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 10, color: 'rgba(148,153,176,0.45)', mt: 1, letterSpacing: '0.04em' }}>
                        Source:{' '}
                        <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(148,153,176,0.7)', textDecoration: 'underline' }}>
                          {source}
                        </a>
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Olympia callout — the bad faith angle */}
            <Box sx={{ p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(255,159,67,0.25)', borderRadius: 3, borderLeft: '3px solid #ff9f43', mb: 5 }}>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#ff9f43', mb: 1.5, letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                The Olympia Precedent — why carriers cannot afford to underwrite blind
              </Typography>
              <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontStyle: 'italic', fontSize: { xs: 14, md: 16 }, color: '#d0d0e0', lineHeight: 1.8 }}>
                "The daycare was severely underinsured. The insurer opposed the settlement with strong objection. The court ruled that plaintiffs could pursue recovery of the full $25 million directly from the insurer in bad faith litigation — regardless of policy limits. Underwriting without visibility into what is happening inside a facility is not risk management. It is exposure."
              </Typography>
            </Box>
          </Box>

          <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontSize: { xs: 15, md: 17 }, color: '#9499b0', lineHeight: 1.8, maxWidth: 700 }}>
            The market is stressed from both sides. Providers cannot afford premiums; carriers cannot confidently price risk because they have no visibility into what is actually happening inside facilities. Cameras exist at most locations — but cameras document abuse after the fact. They do not prevent it, and they do not help you price it.
          </Typography>
        </Container>
      </Box>

      {/* What daycares actually pay */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            The Cost Burden
          </Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            What a daycare actually pays for insurance.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 7, maxWidth: 620 }}>
            General liability is just the start. Abuse and molestation coverage — required by law in many states — is a separate endorsement that does not come with a standard policy. Since 2024, some facilities have seen 300–1,000% premium increases. 62% of operators report difficulty finding affordable coverage at all.
          </Typography>

          {/* Cost tiers */}
          <Grid container spacing={0} sx={{ mb: 5 }}>
            {[
              { type: 'In-home / small', range: '$400 – $2,000', sub: 'Annual general liability', note: 'A&M endorsement extra' },
              { type: 'Small commercial center', range: '$1,100 – $4,000', sub: 'Annual general liability', note: 'A&M endorsement extra' },
              { type: 'Mid-size center', range: '$5,000 – $18,000', sub: 'Annual combined coverage', note: 'Higher with A&M + workers\' comp' },
              { type: 'Large / multi-location', range: '$8,000 – $60,000+', sub: 'Annual combined coverage', note: 'Per-incident exposure rises with size' },
            ].map(({ type, range, sub, note }, i) => (
              <Grid key={type} size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{
                  p: 3,
                  bgcolor: '#13131f',
                  border: '1px solid rgba(92,225,230,0.1)',
                  borderRight: { sm: i % 2 === 0 ? 'none' : '1px solid rgba(92,225,230,0.1)', md: i < 3 ? 'none' : '1px solid rgba(92,225,230,0.1)' },
                  borderBottom: { xs: i < 3 ? 'none' : '1px solid rgba(92,225,230,0.1)', sm: i < 2 ? 'none' : '1px solid rgba(92,225,230,0.1)', md: '1px solid rgba(92,225,230,0.1)' },
                  borderRadius: i === 0 ? { xs: '12px 12px 0 0', md: '12px 0 0 12px' } : i === 3 ? { xs: '0 0 12px 12px', md: '0 12px 12px 0' } : 0,
                  height: '100%',
                }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.5rem', md: '1.7rem' }, color: '#5ce1e6', lineHeight: 1, mb: 1 }}>
                    {range}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 13, color: '#f0f0f8', mb: 0.5 }}>
                    {type}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: '#9499b0', mb: 1 }}>
                    {sub}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, color: '#ff9f43', fontStyle: 'italic' }}>
                    {note}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* A&M callout */}
          <Box sx={{ p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(255,159,67,0.2)', borderRadius: 3, borderLeft: '3px solid #ff9f43', mb: 4 }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 14, color: '#ff9f43', mb: 1 }}>
              Abuse & Molestation Coverage — the line item nobody wants to talk about
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.75 }}>
              Standard general liability explicitly excludes abuse and molestation claims. To be covered, facilities must add a separate A&M endorsement — at additional cost, often with higher deductibles, and with significantly tighter underwriting since 2023. Tennessee, Florida, and other states now require it for licensing. Most operators carry it without understanding what it actually covers. And when a claim hits it, the numbers are not small.
            </Typography>
          </Box>

          <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontSize: { xs: 15, md: 17 }, color: '#9499b0', lineHeight: 1.8, maxWidth: 700 }}>
            The carrier that can price A&M risk with confidence — backed by verified behavioral monitoring data — gains an underwriting advantage that does not exist anywhere else in the market today.
          </Typography>
        </Container>
      </Box>

      {/* How partnership works */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            The Model
          </Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            Lower risk. Revenue share. Competitive differentiation.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 7, maxWidth: 580 }}>
            The structure is straightforward. The outcome for your book of business is not incremental — it is categorical.
          </Typography>

          <Stack spacing={2.5}>
            {[
              {
                num: '1',
                title: 'SMCLA deploys at your insured daycares',
                body: 'Real-time AI monitoring detects physical abuse, emotional abuse, and safety hazards before they escalate into claims. Your policyholders run demonstrably safer facilities — and you can verify it.',
              },
              {
                num: '2',
                title: 'You offer premium discounts to SMCLA customers',
                body: 'Verified monitoring reduces claims frequency and severity. A discount tier for SMCLA-monitored daycares is actuarially justifiable, competitively powerful, and something no competitor in your market currently offers.',
              },
              {
                num: '3',
                title: 'You earn a revenue share on every deployment',
                body: 'For each SMCLA deployment within your book of business, you receive a referral fee. Safety reduces your loss ratio; the partnership generates a new revenue stream on top of that.',
              },
            ].map(({ num, title, body }) => (
              <Box key={num} sx={{ display: 'flex', gap: 3, p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.1)', borderRadius: 3, alignItems: 'flex-start', transition: 'border-color 0.2s', '&:hover': { borderColor: 'rgba(92,225,230,0.25)' } }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 16, color: '#5ce1e6' }}>{num}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 16, color: '#f0f0f8', mb: 1 }}>
                    {title}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>
                    {body}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Risk reduction case */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Why Claims Decrease
          </Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            SMCLA catches what causes claims — before they happen.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 7, maxWidth: 580 }}>
            Every major claim category in childcare liability is detectable before it becomes a filed claim. That is the core actuarial proposition.
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ p: 3.5, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2.5 }}>
                  <ShieldOutlinedIcon sx={{ color: '#5ce1e6', fontSize: 20 }} />
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: '#5ce1e6' }}>
                    Physical & Injury Claims
                  </Typography>
                </Box>
                <Stack spacing={1.5}>
                  {[
                    'Physical aggression and abnormal contact → flagged in real time',
                    'Rough handling and force anomalies → alert before injury occurs',
                    'Unauthorized isolation → detected and logged',
                    'Safety hazards: falls, unsafe practices → flagged as environmental risks',
                    'Escalating behavior patterns identified across sessions before incident',
                  ].map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                      <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#5ce1e6', mt: 0.75, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.6 }}>{item}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ p: 3.5, bgcolor: '#13131f', border: '1px solid rgba(255,159,67,0.2)', borderRadius: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2.5 }}>
                  <VolumeUpIcon sx={{ color: '#ff9f43', fontSize: 20 }} />
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: '#ff9f43' }}>
                    High-Severity & Class-Action Risk
                  </Typography>
                </Box>
                <Stack spacing={1.5}>
                  {[
                    'Verbal threats and degrading language → audio AI detection',
                    'Repeated humiliation of same child across sessions → pattern flagged',
                    'These cases drive the $500k–$16M settlements',
                    'Multi-victim, chronic abuse → class-action territory',
                    'Currently invisible to every other monitoring system on the market',
                  ].map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                      <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#ff9f43', mt: 0.75, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.6 }}>{item}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3, borderLeft: '3px solid #5ce1e6' }}>
            <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontStyle: 'italic', fontSize: { xs: 15, md: 17 }, color: '#d0d0e0', lineHeight: 1.75 }}>
              "The cases that generate $1M+ settlements are almost never single incidents. They are patterns — months of repeated harm by the same worker against the same children. SMCLA is the only system trained to detect those patterns across sessions. That is where your largest claim exposure lives."
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Carrier benefits */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Carrier Benefits
          </Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            The only carrier offering SMCLA-verified rate tiers.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 7, maxWidth: 560 }}>
            This is not a loss control add-on. It is a new category of underwriting signal — and the carrier that prices it first wins the safest segment of the market.
          </Typography>

          <Grid container spacing={3}>
            {carrierBenefits.map(({ icon, title, body }) => (
              <Grid key={title} size={{ xs: 12, sm: 6 }}>
                <Box sx={{ p: 3.5, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.08)', borderRadius: 3, height: '100%', transition: 'border-color 0.2s', '&:hover': { borderColor: 'rgba(92,225,230,0.2)' } }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2 }}>
                    {icon}
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: '#f0f0f8' }}>
                      {title}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>
                    {body}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Design partner program */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Box sx={{ p: { xs: 3, md: 5 }, bgcolor: '#13131f', border: '1px solid rgba(255,159,67,0.25)', borderRadius: 3 }}>
            <Typography variant="overline" sx={{ display: 'block', color: '#ff9f43', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
              Design Partner Phase
            </Typography>
            <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.6rem', md: '2.2rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2.5 }}>
              We need one carrier to build this with us.
            </Typography>
            <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontSize: { xs: 15, md: 17 }, color: '#9499b0', lineHeight: 1.8, mb: 4 }}>
              SMCLA is at design partner stage. We have the product, the detection capability, and the deployment track record. What we need is a carrier willing to co-develop the premium discount framework, share claims data to validate risk reduction, and be the named founding partner in what becomes the industry standard for childcare liability risk.
            </Typography>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 4 }} />

            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 14, color: '#ff9f43', letterSpacing: '0.04em', textTransform: 'uppercase', mb: 2 }}>
                  What the pilot includes
                </Typography>
                <Stack spacing={1.5}>
                  {[
                    '6-month pilot across a subset of your insured daycare portfolio',
                    'Co-development of premium discount tier with your actuarial team',
                    'Shared incident and claims data analysis to quantify risk reduction',
                    'Joint announcement — named as SMCLA\'s founding insurance partner',
                    'Preferred revenue share terms locked in for founding partner only',
                  ].map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                      <CheckCircleIcon sx={{ color: '#ff9f43', fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#d0d0e0', lineHeight: 1.6 }}>
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 14, color: '#9499b0', letterSpacing: '0.04em', textTransform: 'uppercase', mb: 2 }}>
                  What we ask
                </Typography>
                <Stack spacing={1.5}>
                  {[
                    'Access to a subset of your daycare book (minimum 10 locations)',
                    'Actuarial team engagement to co-build the discount model',
                    'Commitment to publish joint findings after the pilot concludes',
                  ].map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                      <Box sx={{ color: '#9499b0', fontWeight: 700, fontSize: 16, flexShrink: 0, lineHeight: 1.5 }}>›</Box>
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.6 }}>
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Grid>
            </Grid>

            <Box sx={{ mt: 4, textAlign: { xs: 'left', md: 'center' } }}>
              <Button
                variant="contained"
                size="large"
                onClick={toContact}
                sx={{ px: 5, py: 1.5, fontWeight: 700, fontSize: 15, bgcolor: '#ff9f43', color: '#0a0a12', borderRadius: 2, '&:hover': { bgcolor: '#e8903a' } }}
              >
                Apply to Be Our Design Partner →
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Final CTA */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(92,225,230,0.1)', py: { xs: 10, md: 14 }, px: 3, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.8rem' }, color: '#f0f0f8', letterSpacing: '-0.03em', mb: 2, lineHeight: 1.1 }}>
            This is not a product demo.<br />It is a partnership conversation.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 16, md: 18 }, color: '#9499b0', lineHeight: 1.7, mb: 5 }}>
            One carrier will co-shape the first AI-verified safety discount program in childcare insurance. We are ready to start that conversation.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={toContact}
            sx={{ px: 5, py: 1.75, fontWeight: 700, fontSize: 17, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, boxShadow: '0 0 32px rgba(92,225,230,0.2)', '&:hover': { bgcolor: '#4ecfd4' } }}
          >
            Request a Partnership Conversation →
          </Button>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: 'rgba(148,153,176,0.55)', mt: 2 }}>
            We respond to every carrier inquiry within 48 hours.
          </Typography>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
